import { useState } from 'react';

import { Button } from '../../button';

import {
  ABSTAIN_QUESTIONS,
  ABSTAIN_REFINEMENT,
  ABSTAINED_TEXT,
  abstainExpected,
  abstainLabels,
  abstainRuns,
  abstainSummary,
  abstainTransition,
  answerChecks,
  answerKind,
  BEST_SEARCH,
  CITE_ANSWERS,
  CITE_REFINEMENT,
  citationMarks,
  citations,
  citeAnswers,
  citedIds,
  citeLabels,
  CITES_EXPECTED_TEXT,
  citeSummary,
  citeTransition,
  expectedRetrieved,
  FAITHFUL_TEXT,
  failures,
  GROUNDING_RULE,
  holdsText,
  initialAbstain,
  initialCite,
  initialMetrics,
  initialPrompt,
  MEASURES,
  measureLabels,
  MEMORY_RULE,
  METRICS_REFINEMENT,
  metricsSummary,
  metricsTransition,
  MISSES,
  NO_PIECES,
  piecesText,
  PROMPT_QUESTIONS,
  PROMPT_REFINEMENT,
  promptAnswer,
  promptPieces,
  promptSummary,
  promptTransition,
  Q1_CHECK_NOTE,
  Q12_NOTE,
  recallRows,
  retrievedText,
  systemLabel,
  toAbstainQuestion,
  toCiteAnswer,
  toMeasure,
  toPromptQuestion,
  type AbstainAction,
  type CiteAction,
  type MetricsAction,
  type PromptAction,
} from './ground-models';
import { questionText } from './know-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './ground-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function GroundPromptLab() {
  const [state, setState] = useState(initialPrompt);
  const act = (action: PromptAction) => setState((current) => promptTransition(current, action));
  const pieces = promptPieces[state.question];
  const summary = promptSummary(state);
  return (
    <Lab
      title="What goes into a grounded prompt?"
      eyebrow="Grounding · the prompt from the pieces"
      className="rel-lab ground-lab"
    >
      <Predict>
        What goes into a grounded prompt? Choose a question, then press Build the prompt.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toPromptQuestion(value) })}
            options={PROMPT_QUESTIONS.map((id) => ({
              value: id,
              label: `${id} ${questionText(id)}`,
            }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.grounded}
          onClick={() => act({ type: 'toggle-grounding' })}
        >
          Grounding rule and pieces
        </Button>
        <Button size="sm" disabled={state.built} onClick={() => act({ type: 'build' })}>
          Build the prompt
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="System message">
        <pre aria-label="System message">{state.grounded ? GROUNDING_RULE : MEMORY_RULE}</pre>
      </Panel>
      <Panel label="Pieces">
        <pre aria-label="Pieces">{state.grounded ? piecesText(pieces) : NO_PIECES}</pre>
      </Panel>
      <div className="cb-two">
        <Panel label="Question">
          <pre aria-label="Question">{questionText(state.question)}</pre>
        </Panel>
        <Panel label="The answer">
          <pre aria-label="The answer">
            {state.built ? promptAnswer(state.question, state.grounded) : '(not built yet)'}
          </pre>
        </Panel>
      </div>
      {state.built ? (
        <Facts
          values={[
            ['System message', systemLabel(state.grounded)],
            ['Pieces sent', String(state.grounded ? pieces.length : 0)],
            ['Answer kind', answerKind(state.question, state.grounded)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{PROMPT_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function GroundCiteLab() {
  const [state, setState] = useState(initialCite);
  const act = (action: CiteAction) => setState((current) => citeTransition(current, action));
  const answer = citeAnswers[state.answer];
  const summary = citeSummary(state);
  return (
    <Lab
      title="Where did each fact come from?"
      eyebrow="Grounding · cite the piece"
      className="rel-lab ground-lab"
    >
      <Predict>
        Does each cited piece hold its fact? Choose an answer, then press Check the citations.
      </Predict>
      <div className="cb-controls">
        <label>
          Answer
          <ModelSelect
            aria-label="Answer"
            value={state.answer}
            onValueChange={(value) => act({ type: 'answer', value: toCiteAnswer(value) })}
            options={CITE_ANSWERS.map((id) => ({ value: id, label: citeLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.checked} onClick={() => act({ type: 'check' })}>
          Check the citations
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The answer">
        <pre aria-label="The answer">{answer}</pre>
      </Panel>
      <Table
        label="Citations"
        columns={['Mark', 'Document', 'Paragraph', 'Holds the fact']}
        template="minmax(0, 0.5fr) minmax(0, 2fr) minmax(0, 0.7fr) minmax(0, 0.8fr)"
        rows={
          state.checked
            ? citations(state.answer).map((row) => [row.mark, row.title, row.paragraph, row.holds])
            : []
        }
      />
      {state.checked ? (
        <Facts
          values={[
            ['Marks', String(citationMarks(answer).length)],
            ['Pieces cited', String(citedIds(answer).length)],
            ['Hold the fact', holdsText(state.answer)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.checked && state.answer === 'q12' ? <p className="rel-caption">{Q12_NOTE}</p> : null}
      {state.checked ? <p className="rel-caption">{CITE_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function GroundAbstainLab() {
  const [state, setState] = useState(initialAbstain);
  const act = (action: AbstainAction) => setState((current) => abstainTransition(current, action));
  const run = abstainRuns[state.question];
  const summary = abstainSummary(state);
  return (
    <Lab
      title="When should the assistant say it does not know?"
      eyebrow="Grounding · the abstention"
      className="rel-lab ground-lab"
    >
      <Predict>Is this abstention right? Choose a question, then press Ask.</Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toAbstainQuestion(value) })}
            options={ABSTAIN_QUESTIONS.map((id) => ({ value: id, label: abstainLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="Pieces retrieved">
        <pre aria-label="Pieces retrieved">
          {state.asked ? retrievedText(run) : '(not asked yet)'}
        </pre>
      </Panel>
      <Panel label="The answer">
        <pre aria-label="The answer">{state.asked ? run.answer : '(not asked yet)'}</pre>
      </Panel>
      {state.asked ? (
        <Facts
          values={[
            ['Expected', abstainExpected(state.question)],
            ['Expected retrieved', expectedRetrieved(state.question)],
            ['Verdict', run.verdict],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{ABSTAIN_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function GroundMetricsLab() {
  const [state, setState] = useState(initialMetrics);
  const act = (action: MetricsAction) => setState((current) => metricsTransition(current, action));
  const summary = metricsSummary(state);
  return (
    <Lab
      title="What do recall and the answer check say?"
      eyebrow="Grounding · measure retrieval first"
      className="rel-lab ground-lab"
    >
      <Predict>Which search measures best? Choose a Measure.</Predict>
      <div className="cb-controls">
        <label>
          Measure
          <ModelSelect
            aria-label="Measure"
            value={state.measure}
            onValueChange={(value) => act({ type: 'measure', value: toMeasure(value) })}
            options={MEASURES.map((measure) => ({ value: measure, label: measureLabels[measure] }))}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      {state.measure === 'recall' ? (
        <>
          <Table
            label="Recall@5"
            columns={['Search', 'Expected piece in the top five']}
            rows={recallRows()}
          />
          <Facts
            values={[
              ['Answerable questions', '17'],
              ['Best search here', BEST_SEARCH],
              ['Hybrid rescued, then lost', 'q8, q1'],
            ]}
          />
        </>
      ) : null}
      {state.measure === 'failures' ? (
        <>
          <Table
            label="Failures"
            columns={['Question', 'Kind', 'Search', 'Why']}
            template="minmax(0, 0.5fr) minmax(0, 1fr) minmax(0, 0.7fr) minmax(0, 2.4fr)"
            rows={failures.map((row) => [row.question, row.kind, row.search, row.why])}
          />
          <Facts
            values={[
              ['Misses', String(MISSES)],
              ['Capability failures', String(MISSES)],
              ['Right abstentions', '1'],
            ]}
          />
        </>
      ) : null}
      {state.measure === 'answers' ? (
        <>
          <Table
            label="The answer check"
            columns={['Question', 'Faithful', 'Cites expected', 'Abstained']}
            template="minmax(0, 0.5fr) minmax(0, 0.6fr) minmax(0, 1.6fr) minmax(0, 1.2fr)"
            rows={answerChecks.map((row) => [
              row.question,
              row.faithful,
              row.citesExpected,
              row.abstained,
            ])}
          />
          <Facts
            values={[
              ['Faithful', FAITHFUL_TEXT],
              ['Cite an expected source', CITES_EXPECTED_TEXT],
              ['Abstained when expected', ABSTAINED_TEXT],
            ]}
          />
          <p className="rel-caption">{Q1_CHECK_NOTE}</p>
        </>
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.measure === 'recall' ? null : <p className="rel-caption">{METRICS_REFINEMENT}</p>}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
